import type {
  ApiCandidate,
  ApiEmployee,
  Employee,
  EmployeeActiveDisplay,
  EmployeeBackendStatus,
} from "./employeeTypes";
import { EMPLOYEE_ID_PLACEHOLDER } from "./employeeTypes";

const EMPTY_CELL = "—";

function splitDisplayNameForPrefill(
  displayName: string | null | undefined,
): { firstName: string; lastName: string } {
  const name = (displayName || "").trim();
  if (!name) {
    return { firstName: "", lastName: "" };
  }
  const space = name.indexOf(" ");
  if (space === -1) {
    return { firstName: name, lastName: "" };
  }
  return {
    firstName: name.slice(0, space),
    lastName: name.slice(space + 1).trim(),
  };
}

function resolveEmployeeNames(row: ApiEmployee): { firstName: string; lastName: string } {
  const firstName = (row.firstName || "").trim();
  const lastName = (row.lastName || "").trim();
  if (firstName || lastName) {
    return { firstName, lastName };
  }
  return splitDisplayNameForPrefill(row.displayName);
}

function formatCellValue(value: string | null | undefined): string {
  const trimmed = (value || "").trim();
  return trimmed || EMPTY_CELL;
}

function formatHireDate(
  createdAt: string | null | undefined,
  hireDate: string | null | undefined,
): string {
  if (hireDate) {
    const date = hireDate.slice(0, 10);
    return date || EMPTY_CELL;
  }
  if (!createdAt) return EMPTY_CELL;
  const date = createdAt.slice(0, 10);
  return date || EMPTY_CELL;
}

export function activeDisplayForStatus(
  status: EmployeeBackendStatus,
): EmployeeActiveDisplay {
  switch (status) {
    case "active":
      return "Yes";
    case "candidate":
      return "Pending Employee";
    case "pending":
      return "Awaiting Activation";
    case "suspended":
      return "Suspended";
    case "deleted":
      return "Deleted";
    default:
      return "Awaiting Activation";
  }
}

function normalizeBackendStatus(
  status: string | null | undefined,
): EmployeeBackendStatus {
  if (
    status === "candidate" ||
    status === "pending" ||
    status === "active" ||
    status === "suspended" ||
    status === "deleted"
  ) {
    return status;
  }
  return "pending";
}

export function mapApiEmployeeToRow(row: ApiEmployee): Employee {
  const backendStatus = normalizeBackendStatus(row.status);
  const email = (row.emailAddress || "").trim();
  const { firstName, lastName } = resolveEmployeeNames(row);
  const employeeId = row.employeeId || EMPLOYEE_ID_PLACEHOLDER;
  const auth0Sub = row.auth0Sub || "";

  return {
    employeeId,
    firstName,
    lastName,
    email,
    department: formatCellValue(row.departmentDisplayName || row.department),
    role: row.roleDisplayName || row.employeeRoleKey || EMPTY_CELL,
    active: backendStatus === "active",
    activeDisplay: activeDisplayForStatus(backendStatus),
    hireDate: formatHireDate(row.createdAt, row.hireDate),
    manager: formatCellValue(row.manager),
    city: "",
    state: "",
    region: "",
    auth0Sub,
    backendStatus,
    rowKey: employeeId !== EMPLOYEE_ID_PLACEHOLDER ? employeeId : auth0Sub,
    latestLifecycleReason: row.latestLifecycleReason || null,
    latestLifecycleEventType: row.latestLifecycleEventType || null,
    latestLifecycleAt: row.latestLifecycleAt || null,
    latestLifecycleBy: row.latestLifecycleBy || null,
    latestLifecycleByName: row.latestLifecycleByName || null,
    latestLifecycleByEmail: row.latestLifecycleByEmail || null,
    suspensionReason: row.suspensionReason || null,
  };
}

export function mapApiCandidateToRow(row: ApiCandidate): Employee {
  const email = (row.emailAddress || "").trim();
  const { firstName, lastName } = splitDisplayNameForPrefill(row.displayName);
  const backendStatus: EmployeeBackendStatus = "candidate";

  return {
    employeeId: EMPLOYEE_ID_PLACEHOLDER,
    firstName,
    lastName,
    email,
    department: EMPTY_CELL,
    role: EMPTY_CELL,
    active: false,
    activeDisplay: activeDisplayForStatus(backendStatus),
    hireDate: EMPTY_CELL,
    manager: EMPTY_CELL,
    city: "",
    state: "",
    region: "",
    auth0Sub: row.auth0Sub,
    backendStatus,
    rowKey: row.auth0Sub,
  };
}

const CANONICAL_EMP_ID_PATTERN = /^EMP-(\d+)$/;

export type EmployeeTableSortColumn =
  | "employeeId"
  | "name"
  | "email"
  | "department"
  | "role"
  | "manager"
  | "active"
  | "hireDate"
  | "notes";
export type EmployeeTableSortDirection = "asc" | "desc";

function isEmptySortCell(value: string | null | undefined): boolean {
  const trimmed = (value || "").trim();
  return !trimmed || trimmed === EMPTY_CELL;
}

function compareTextCells(
  left: string,
  right: string,
  multiplier: number,
): number {
  const normalize = (value: string) => {
    if (isEmptySortCell(value)) return "";
    return value.trim().toLowerCase();
  };
  const a = normalize(left);
  const b = normalize(right);
  if (!a && !b) return 0;
  if (!a) return multiplier;
  if (!b) return -multiplier;
  return a.localeCompare(b) * multiplier;
}

function compareDateCells(
  left: string,
  right: string,
  multiplier: number,
): number {
  const parse = (value: string) => {
    if (isEmptySortCell(value)) return null;
    const timestamp = Date.parse(value.slice(0, 10));
    return Number.isNaN(timestamp) ? null : timestamp;
  };
  const a = parse(left);
  const b = parse(right);
  if (a === null && b === null) return 0;
  if (a === null) return multiplier;
  if (b === null) return -multiplier;
  return (a - b) * multiplier;
}

function notesSortValue(row: Employee): string {
  if (row.latestLifecycleReason?.trim()) {
    return row.latestLifecycleReason.trim();
  }
  if (row.backendStatus === "candidate") {
    return "Pending Employee";
  }
  if (row.backendStatus === "pending") {
    return "Awaiting Activation";
  }
  return "";
}

function compareByEmployeeIdTieBreak(a: Employee, b: Employee, multiplier: number): number {
  const idDiff = parseEmployeeIdSortKey(a.employeeId) - parseEmployeeIdSortKey(b.employeeId);
  if (idDiff !== 0) {
    return idDiff * multiplier;
  }
  return a.employeeId.localeCompare(b.employeeId) * multiplier;
}

export function parseEmployeeIdSortKey(employeeId: string): number {
  const match = CANONICAL_EMP_ID_PATTERN.exec(String(employeeId || ""));
  return match ? Number.parseInt(match[1], 10) : Number.MAX_SAFE_INTEGER;
}

export function sortEmployeeTableRows(
  rows: Employee[],
  column: EmployeeTableSortColumn,
  direction: EmployeeTableSortDirection,
): Employee[] {
  const multiplier = direction === "asc" ? 1 : -1;

  return [...rows].sort((a, b) => {
    let diff = 0;

    switch (column) {
      case "employeeId":
        return compareByEmployeeIdTieBreak(a, b, multiplier);
      case "name":
        diff = compareTextCells(
          `${a.firstName} ${a.lastName}`.trim(),
          `${b.firstName} ${b.lastName}`.trim(),
          1,
        );
        break;
      case "email":
        diff = compareTextCells(a.email, b.email, 1);
        break;
      case "department":
        diff = compareTextCells(a.department, b.department, 1);
        break;
      case "role":
        diff = compareTextCells(a.role, b.role, 1);
        break;
      case "manager":
        diff = compareTextCells(a.manager, b.manager, 1);
        break;
      case "active":
        diff = compareTextCells(a.activeDisplay, b.activeDisplay, 1);
        break;
      case "hireDate":
        diff = compareDateCells(a.hireDate, b.hireDate, 1);
        break;
      case "notes":
        diff = compareTextCells(notesSortValue(a), notesSortValue(b), 1);
        break;
      default:
        diff = 0;
    }

    if (diff !== 0) {
      return diff * multiplier;
    }

    return compareByEmployeeIdTieBreak(a, b, multiplier);
  });
}

export function isCanonicalEmployeeId(employeeId: string | null | undefined): boolean {
  return CANONICAL_EMP_ID_PATTERN.test(String(employeeId || ""));
}

/** Rows shown in the main operational Employees table (active and suspended only). */
export function isPrimaryEmployeeRow(row: Employee): boolean {
  return (
    isCanonicalEmployeeId(row.employeeId) &&
    (row.backendStatus === "active" || row.backendStatus === "suspended")
  );
}

/** Lower-priority rows collapsed at the bottom (pending employees, missing EMP id, etc.). */
export function isHiddenEmployeeRow(row: Employee): boolean {
  return !isPrimaryEmployeeRow(row);
}

function sortEmployeeRows(rows: Employee[]): Employee[] {
  return sortEmployeeTableRows(rows, "employeeId", "asc");
}

export function mergeEmployeeRows(
  employees: ApiEmployee[],
  candidates: ApiCandidate[],
): Employee[] {
  const enrolledSubs = new Set(
    employees
      .map((row) => (row.auth0Sub || "").trim())
      .filter(Boolean),
  );
  const pendingCandidates = candidates.filter(
    (candidate) => !enrolledSubs.has(candidate.auth0Sub),
  );

  return sortEmployeeRows([
    ...employees.map(mapApiEmployeeToRow),
    ...pendingCandidates.map(mapApiCandidateToRow),
  ]);
}

export function splitEmployeeRows(
  employees: ApiEmployee[],
  candidates: ApiCandidate[],
): { primary: Employee[]; hidden: Employee[] } {
  const primary: Employee[] = [];
  const hidden: Employee[] = [];

  for (const row of mergeEmployeeRows(employees, candidates)) {
    if (isPrimaryEmployeeRow(row)) {
      primary.push(row);
    } else {
      hidden.push(row);
    }
  }

  return { primary, hidden };
}
